import React from 'react';
import { AreaChart } from '@tremor/react';
import { formatPrice } from '@dashboard/services/formatting';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import { useStatisticsContext } from '../Dashboard';
import { formatTimestamp, formatValue } from '../dates';
import { useStatistics } from '../queries';
import { isNumber } from 'lodash';
import ChartMeta from './ChartMeta';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import Spinner from '@dashboard/components/Spinner';

export default function Revenue() {
  const { unit, range } = useStatisticsContext();
  const { data, isLoading } = useStatistics('revenue');

  const revenueData = data?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Revenue: formatValue(timestamp, value),
  }));

  const totalRevenue = data?.reduce((acc, { value }) => acc + value, 0);

  const maxValue = data && Math.max(...data?.map(({ value }) => value), 0);
  const maxValueFormatted = formatPrice({
    amount: maxValue,
    currency: 'USD',
  });
  const minValue = data && Math.min(...data?.map(({ value }) => value), 0);
  const minValueFormatted = formatPrice({
    amount: minValue,
    currency: 'USD',
  });

  return (
    <Card variant="none">
      <CardHeader variant="card">
        <CardHeaderTitle variant="card">
          <div>
            <div className="text-base font-normal">
              Revenue
              <InfoTooltip>
                Gross revenue from one-time payments and subscriptions before refunds and Stripe
                fees.
              </InfoTooltip>
            </div>

            <div className="text-xl mt-1.5 font-normal">
              {formatPrice({
                amount: totalRevenue,
                currency: 'USD',
              })}
            </div>
          </div>
        </CardHeaderTitle>
      </CardHeader>

      <CardContent variant="root">
        {isLoading ? (
          <div className="flex items-center justify-center h-44">
            <Spinner />
          </div>
        ) : (
          <div className="relative pl-8">
            {revenueData && <ChartMeta maxValue={maxValueFormatted} minValue={minValueFormatted} />}

            <AreaChart
              className="mt-4 h-44"
              data={revenueData}
              categories={['Revenue']}
              index="timestamp"
              colors={['amber']}
              valueFormatter={value =>
                isNumber(value)
                  ? formatPrice({
                      amount: value,
                      currency: 'USD',
                    })
                  : value
              }
              showAnimation
              allowDecimals={false}
              showLegend={false}
              showGridLines={false}
              showYAxis={false}
              showXAxis
              startEndOnly
              showGradient
              padding={{ left: 16, right: 16 }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
